import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import invoiceService from "../services/invoiceService";

/**
 * Custom hook to manage invoice state and operations
 */
export const useInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  /**
   * Loads invoices from the API
   */
  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    try {
      const data = await invoiceService.fetchInvoices(page, fromDate, toDate);

      if (data.data) {
        setInvoices(data.data);
        setCurrentPage(data.current_page);
        setTotalPages(data.last_page);
        setTotal(data.total);
        setFrom(data.from);
        setTo(data.to);
      } else {
        const invoicesData = data ?? [];
        setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      }
    } catch (error) {
      console.error("Error loading invoices:", error);
      Swal.fire("Error", "Could not load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches all invoices (for export)
   */
  const fetchAllInvoices = async () => {
    const data = await invoiceService.fetchAllInvoices(fromDate, toDate);
    return Array.isArray(data) ? data : [];
  };

  /**
   * Changes page
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchInvoices(newPage);
    }
  };

  /**
   * Applies search filters
   */
  const handleSearch = () => {
    setCurrentPage(1);
    fetchInvoices(1);
  };

  /**
   * Clears filters
   */
  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    fetchInvoices(1);
  };

  // Load invoices on mount
  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // State
    invoices,
    loading,
    fromDate,
    toDate,
    currentPage,
    totalPages,
    total,
    from,
    to,

    // Setters
    setFromDate,
    setToDate,

    // Functions
    fetchInvoices,
    fetchAllInvoices,
    handlePageChange,
    handleSearch,
    handleClearFilters,
  };
};